/* istanbul ignore file */
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { px, rem, Theme } from '../../../theme';

import { TextInputProperties } from './TextInput.types';

const errorStyle = (theme: Theme) => `
    border-color: ${theme.palette.error.main};
    box-shadow: ${theme.tggSpacing([0, 0, 0, rem(0.1)], [0, 0, 0, 0.25])} ${theme.palette.error.main};
`;

const greyStyle = (theme: Theme, isInvalid?: boolean) => `
    opacity: 0.4;
    background-color: ${theme.palette.grey['50']};
    ${isInvalid ? errorStyle(theme) : `border: none;`}
`;

const whiteStyle = (theme: Theme, isInvalid?: boolean) => `
    background-color: ${theme.palette.common.white};
    ${isInvalid ? errorStyle(theme) : `border-color: ${theme.palette.common.blue};`}
`;

const transparentStyle = (theme: Theme, isInvalid?: boolean) => `
    background-color: transparent;
    ${isInvalid ? errorStyle(theme) : `border-color: transparent;`}
`;

const variantStyles = {
    grey: greyStyle,
    white: whiteStyle,
    transparent: transparentStyle,
};

export const StyledTextInputContainer = styled('div')<{
    $variant: NonNullable<TextInputProperties['variant']>;
    $isInvalid: TextInputProperties['isInvalid'];
    $multiline: TextInputProperties['multiline'];
    theme: Theme;
}>`
    ${({ theme, $variant, $isInvalid, $multiline }) => `
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        ${!$multiline && `height: ${theme.tggSpacing(rem(4.4), 11)};`}
        padding: ${theme.tggSpacing([rem(0.9), rem(1.5)], [2.25, 3.75])};
        border-style: solid;
        border-width: ${theme.tggSpacing(rem(0.1), 0.25)};
        border-radius: ${theme.tggSpacing(rem(2.5), 6)};
        ${variantStyles[$variant as keyof typeof variantStyles](
            theme,
            $isInvalid,
        )}
        ${
            $variant === 'grey'
                ? `
                    svg g {
                        fill: ${theme.palette.grey['900']};
                    }
                `
                : ''
        }
	`}
`;

export const StyledTextInput = styled(TextField)`
    ${({ theme }: { theme: Theme }) => theme.typography.text_16};
    ${({ theme, multiline, select }) => `
        flex-direction: unset;
        width: 100%;
        padding-left: ${theme.tggSpacing(rem(1), 2.5)};
        color: ${theme.palette.common.blue};
        background: none;
        border: none;
        outline: none;

        input,
        fieldset {
            padding: ${theme.tggSpacing(0, 0)};
            border: none;
        }

        ${
            multiline
                ? `
                .MuiOutlinedInput-multiline {
                    padding: ${theme.tggSpacing([rem(0.5), 0], [1.25, 0])};
                }
            `
                : ``
        }

        ${
            select
                ? `
        svg.MuiSelect-iconOutlined{
            right: ${theme.tggSpacing(0, 0)};
        }

        select {
            width: 100%;
            padding: ${theme.tggSpacing([rem(0.9), 0], [2.25, 0])};

            scrollbar-color: ${theme.palette.primary.main}
                transparent;
            &::-webkit-scrollbar {
                width: ${theme.tggSpacing(rem(0.6), 1.5)};
            }
            &::-webkit-scrollbar-track {
                background-color: ${theme.palette.grey[100]};
            }
            &::-webkit-scrollbar-thumb {
                background-color: ${theme.palette.primary.main};
                border-radius: ${theme.tggSpacing(rem(0.2), 1)};
            }

            :focus {
                background-color: unset;
            }
        }`
                : ``
        }
	`}
`;

export const customStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            width: '100%',
        },
        input: {
            color: theme.palette.common.blue,
            '&::placeholder': {
                color: theme.palette.common.blue,
                opacity: 1,
            },
        },
    }),
);

export const StyledErrorMessage = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        margin-top: ${theme.tggSpacing(px(5), 1.25)};
        color: ${theme.palette.error.main};
        text-transform: lowercase;
	`}
`;

export const StyledLabel = styled('label')<{
    $variant?: NonNullable<TextInputProperties['variant']>;
    theme: Theme;
}>`
    ${({ theme, $variant }) => `
        display: inline-block;
        margin-bottom: ${theme.tggSpacing(rem(0.5), 1.25)};
        font-weight: normal;
        color: ${theme.palette.common.blue};
        text-transform: lowercase;
        ${$variant === 'grey' ? 'opacity: 0.4;' : ''}
	`}
`;

export const StyledSideIconContainer = styled('span')`
    line-height: 0;
`;
